import { useCallback, useEffect, useState } from 'react';
import {
	Promotion,
	trackSportsbookPromotionCarouselClick,
	trackSportsbookPromotionCarouselImpression
} from '@comeon/analytics';

import { useTypedTranslations } from '~/components/translation/use-translations';
import { isSmallerSelector } from '~/components/layout/mediaquery';

import { OddsFormat } from '../../sportsbook-types';
import { RecommendedBetDto } from '~/components/sportsbook/lobby/api/sbapi/sbapi-api-model';
import SportsbookCarousel from '../carousel/sportsbook-carousel';
import BetRecommendationsBetTextKeys from './bet-recommendations-bet/sportsbook-bet-recommendations-bet-text-keys';
import SportsbookTextKeys from '~/components/sportsbook/lobby/sportsbook-text-keys';

import BetRecommendationsBetSkeleton from './bet-recommendations-bet/sportsbook-bet-recommendations-bet-skeleton';
import BetRecommendationsBet from './bet-recommendations-bet/sportsbook-bet-recommendations-bet';
import BetRecommendationsBetSuccess from './bet-recommendations-bet/sportsbook-bet-recommendations-bet-success-overlay';
import BetRecommendationsBetWrapper from './bet-recommendations-bet/sportsbook-bet-recommendations-bet-wrapper';
import { useAppSelector } from '~/store/types';

const skeletons = Array(3)
	.fill(0)
	.map((_, i) => `recommnded-bet-skeleton-key-${i}`);

const mapBetToPromoItem = (recommendedBet: RecommendedBetDto) => {
	return {
		id: `OptiX_${recommendedBet.items.map(item => item.event.id)[0]}`,
		type: recommendedBet.type,
		sportId: recommendedBet.items.map(item => item.event.sportId)[0],
		selectionIds: recommendedBet.items.map(item => item.selection.externalId),
		marketIds: recommendedBet.items.map(item => item.market.externalId)
	} as Promotion;
};

type SportsbookBetRecommendationsCarouselProps = {
	carouselName: string;
	recommendedBets: RecommendedBetDto[];
	isLoading: boolean;
	oddsFormat: OddsFormat;
	onBetClick: (recommendedBet: RecommendedBetDto) => void;
	activeSelectionIds?: string[];
	successSelectionIds?: string[];
	shouldDeepLink?: boolean;
	showArrows?: boolean;
	paddingX?: number;
};

const SportsbookBetRecommendationsCarousel: React.FC<SportsbookBetRecommendationsCarouselProps> = ({
	carouselName,
	recommendedBets,
	isLoading,
	oddsFormat,
	onBetClick,
	activeSelectionIds = [],
	successSelectionIds = [],
	shouldDeepLink = true,
	showArrows = false,
	paddingX = 0
}) => {
	const { translations, isLoadingTranslations } = useTypedTranslations({
		...SportsbookTextKeys,
		...BetRecommendationsBetTextKeys
	});
	const isSmall = useAppSelector(isSmallerSelector);

	const [isImpressionEventLoaded, setIsImpressionEventLoaded] = useState(false);

	// Track impression
	useEffect(() => {
		if (!recommendedBets || recommendedBets.length === 0 || isLoading || isImpressionEventLoaded) {
			return;
		}
		trackSportsbookPromotionCarouselImpression(recommendedBets.map(mapBetToPromoItem), carouselName);
		setIsImpressionEventLoaded(true);
	}, [isImpressionEventLoaded, isLoading, recommendedBets, carouselName]);

	// Track click and propagate
	const handleCarouselItemClick = useCallback(
		(index: number) => {
			const recommendedBet = recommendedBets && recommendedBets[index];
			if (recommendedBet) {
				trackSportsbookPromotionCarouselClick(mapBetToPromoItem(recommendedBet), carouselName, index);
			}
			onBetClick(recommendedBet);
		},
		[recommendedBets, onBetClick, carouselName]
	);

	if (!isLoading && recommendedBets?.length === 0) {
		return null;
	}

	if (isLoadingTranslations || isLoading) {
		return (
			<SportsbookCarousel showArrows={false} gap={isSmall ? 12 : 20} paddingX={paddingX}>
				{skeletons.map(s => (
					<BetRecommendationsBetSkeleton key={s} />
				))}
			</SportsbookCarousel>
		);
	}

	return (
		<SportsbookCarousel showArrows={showArrows} gap={isSmall ? 12 : 20} paddingX={paddingX}>
			{recommendedBets?.flatMap((bet, index) => {
				const isSuccess =
					successSelectionIds.length > 0 &&
					bet.items.every(item => successSelectionIds.includes(item.selection.id));
				const isActive =
					activeSelectionIds.length > 0 &&
					bet.items.every(item => activeSelectionIds.includes(item.selection.id));

				return (
					<BetRecommendationsBetWrapper key={index}>
						<BetRecommendationsBet
							key={index}
							bet={bet}
							shouldDeepLink={shouldDeepLink}
							isInvisible={isSuccess}
							isActive={isActive}
							translations={translations}
							oddsFormat={oddsFormat}
							onClick={handleCarouselItemClick?.bind(this, index)}
						/>
						{isSuccess && <BetRecommendationsBetSuccess translations={translations} />}
					</BetRecommendationsBetWrapper>
				);
			})}
		</SportsbookCarousel>
	);
};

export default SportsbookBetRecommendationsCarousel;
